import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  GroundTruthSingleChoice,
  PartialBy,
  QuestionSingleChoice,
  QuestionTypeV2,
} from '../../redux/types';
import ColorContext from '../../context/ColorContext';
import GTResetButton from './GTResetButton';

type Props = {
  groundTruthId?: number;
  surveyId: number;
  sectionId: number;
  questionId: number;
  caseId: number;
  question: QuestionSingleChoice;
  payload?: GroundTruthSingleChoice['payload'];
  gtGroupId: number|null;
  onSave: (gtData: PartialBy<GroundTruthSingleChoice, 'id'>) => void;
  onCancel: () => void;
  onDelete: (dgtId: number) => void;
};

const getInitalPayload = (question: QuestionSingleChoice) => {
  const initPayload = question.payload?.reduce((acc, cur) => {
    acc[cur.id] = 0;
    return acc;
  }, {} as GroundTruthSingleChoice['payload']);
  return initPayload;
};

const GTSingleChoiceEditor: React.FC<Props> = ({
  groundTruthId,
  surveyId,
  sectionId,
  questionId,
  caseId,
  question,
  payload,
  gtGroupId,
  onSave,
  onCancel,
  onDelete,
}: Props) => {
  const color = useContext(ColorContext);
  const [payloadBuffer, setpayloadBuffer] = useState<GroundTruthSingleChoice['payload'] | undefined>(payload || getInitalPayload(question));

  const saveHandler = () => {
    if (!payloadBuffer) {
      return;
    }
    const newGTRecord: PartialBy<GroundTruthSingleChoice, 'id'> = {
      id: groundTruthId,
      digitalSurveyId: surveyId,
      digitalSectionId: sectionId,
      digitalQuestionId: questionId,
      digitalCaseId: caseId,
      type: QuestionTypeV2.SingleChoice,
      payload: payloadBuffer,
      digitalGroundTruthGroupId: gtGroupId,
    };
    onSave(newGTRecord);
  };

  const cancelHandler = () => {
    onCancel();
  };

  const onScoreChange = (event: React.ChangeEvent<HTMLInputElement>, optionId: number) => {
    const score = parseInt(event.target.value, 10);
    setpayloadBuffer({
      ...payloadBuffer,
      [optionId]: score,
    });
  };

  return (
    <Box>
      <Box sx={{ mx: 2, my: 3 }}>
        <Typography color="textSecondary">{question.text}</Typography>
        <Box>
          {
            payloadBuffer && question.payload?.map((op) => (
              <Box display="flex" gap={3} key={op.id} sx={{ my: 1 }}>
                <Typography gutterBottom component="p" sx={{ flexBasis: 500 }}>{op.text}</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={payloadBuffer[op.id]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScoreChange(e, op.id)}
                />
              </Box>
            ))
          }
        </Box>
      </Box>
      <Divider />
      <Box display="flex" sx={{ mt: 1 }} justifyContent="flex-end" gap={1}>
        <Box flexGrow={1}>
          <GTResetButton groundTruthId={groundTruthId} onReset={onDelete} />
        </Box>
        <Button variant="contained" color={color} onClick={saveHandler}><FormattedMessage id="save" /></Button>
        <Button variant="contained" color="secondary" onClick={cancelHandler}><FormattedMessage id="genericCancel" /></Button>
      </Box>
    </Box>
  );
};

export default GTSingleChoiceEditor;
